import { useState, useEffect } from 'react';
import { useDebounce } from '@hooks';
import { showError } from '@utils';
import { usersServices, subscriptionsServices } from '@services';
import { getSubscriptionsSearchPayload } from '@helpers';
import { formatSubscriptionOption } from 'helpers/sidebarGenericTicket';

export const useSubscriptionSearch = (selectedUser) => {
  const [subscriptionsOptions, setSubscriptionsOptions] = useState([]);
  const [subscriptionSearchTerm, setSubscriptionSearchTerm] = useState('');
  const [isLoadingSubscriptions, setIsLoadingSubscriptions] = useState(false);
  const [isSubscriptionAutocompleteOpen, setIsSubscriptionAutocompleteOpen] =
    useState(false);
  const debouncedSubscriptionSearchInput = useDebounce(
    subscriptionSearchTerm,
    500
  );

  const fetchSubscriptions = async (searchValue) => {
    if (selectedUser) {
      setIsLoadingSubscriptions(true);
      try {
        const response = await usersServices.getUserSubscriptions(
          selectedUser.id
        );
        const subscriptionsResponse = response?.data || [];
        const filteredSubscriptions = subscriptionsResponse
          .filter((sub) => {
            if (!searchValue || !searchValue.trim()) return true;
            const searchLower = searchValue.toLowerCase();
            const vehicleName = `${sub.vehicle?.manufacturer || ''} ${
              sub.vehicle?.model || ''
            }`.toLowerCase();
            const plate = (
              sub.vehiclePlate ||
              sub.vehicle?.plate ||
              ''
            ).toLowerCase();
            return (
              vehicleName.includes(searchLower) ||
              plate.includes(searchLower) ||
              sub.id?.toLowerCase().includes(searchLower)
            );
          })
          .map(formatSubscriptionOption);
        setSubscriptionsOptions(filteredSubscriptions);
      } catch (error) {
        showError(error);
        setSubscriptionsOptions([]);
      } finally {
        setIsLoadingSubscriptions(false);
      }
      return;
    }

    if (!searchValue || !searchValue.trim()) {
      setSubscriptionsOptions([]);
      return;
    }

    setIsLoadingSubscriptions(true);
    try {
      const filterPayload = getSubscriptionsSearchPayload(searchValue);
      const response = await subscriptionsServices.getSubscriptions({
        params: { page: 1, pageSize: 20 },
        payload: filterPayload,
      });
      const subscriptionsResponse = response?.data || [];
      const normalizedSubscriptions = subscriptionsResponse.map(
        formatSubscriptionOption
      );
      setSubscriptionsOptions(normalizedSubscriptions);
    } catch (error) {
      showError(error);
      setSubscriptionsOptions([]);
    } finally {
      setIsLoadingSubscriptions(false);
    }
  };

  useEffect(() => {
    fetchSubscriptions(debouncedSubscriptionSearchInput);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSubscriptionSearchInput, selectedUser]);

  const handleSubscriptionInputChange = (newInputValue, reason) => {
    if (reason === 'input' || reason === 'clear') {
      setSubscriptionSearchTerm(newInputValue || '');
      setIsSubscriptionAutocompleteOpen(
        !!newInputValue && newInputValue.length > 0
      );
    }
  };

  const handleSubscriptionAutocompleteClose = () => {
    setIsSubscriptionAutocompleteOpen(false);
    setSubscriptionSearchTerm('');
    if (!selectedUser) {
      setSubscriptionsOptions([]);
    }
  };

  const clearSearch = () => {
    setSubscriptionSearchTerm('');
    setSubscriptionsOptions([]);
  };

  return {
    subscriptionsOptions,
    subscriptionSearchTerm,
    isLoadingSubscriptions,
    isSubscriptionAutocompleteOpen,
    setIsSubscriptionAutocompleteOpen,
    handleSubscriptionInputChange,
    handleSubscriptionAutocompleteClose,
    fetchSubscriptions,
    clearSearch,
  };
};
